import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import React, {useEffect, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {Box, Button, Typography} from "@mui/material";
import {ColDef} from "ag-grid-community";
import {CustomCellRendererProps} from "ag-grid-react";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {styled} from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {Check, Close} from "@mui/icons-material";

interface ApprovalRequest {
	uid: string
	type: "message" | "variant";
	subject: string
	body: string
	blocked_reason: string
	user_email: string
}

interface PaginationData {
    data: ApprovalRequest[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
}

export default function AdminApprovalRequest() {
	const {enqueueSnackbar} = useSnackbar();
	
	const [pageData, setPageData] = useState<PaginationData>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");

	// Query for fetching salutation words data.
	const pageDataQuery = useQuery({
		queryKey: ["emailContentApprovalRequest", page, pageSize, search],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/email-content-approval-request/?page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (pageDataQuery.data?.data) {
			const response = pageDataQuery.data.data;

			setPageData({
				data: response.data || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
			});
		}
	}, [pageDataQuery.data]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	// Mutation for approving or rejecting request.
	const approveOrRejectMutation = useMutation({
		mutationKey: ["AdminApproveOrRejectRequest"],
		mutationFn: (data: {
			uid: string;
			type: "message" | "variant";
			approved: boolean;
		}) =>
			authenticateAndPostData(
				"/admin/email-content-approval-request/",
				{
					uid: data.uid,
					type: data.type,
					approved: data.approved,
				}
			),

		onSuccess: (response) => {
			const uid = response.data.uid

			const approved = response.data.approved;

			enqueueSnackbar(
				`Message ${uid} has been ${
					approved ? "Approved" : "Rejected"
				}`,
				{
					variant: "success",
				}
			);
			pageDataQuery.refetch();
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const BodyMessageDialog = (params: CustomCellRendererProps) => {
		const [open, setOpen] = useState(false);
		const [viewMode, setViewMode] = useState<"raw" | "html">("raw");

		const handleClickOpen = () => {
			setViewMode("raw");
			setOpen(true);
		};

		const handleClose = () => setOpen(false);
		const isHTML = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text);

		const BootstrapDialog = styled(Dialog)(({ theme }) => ({
			"& .MuiDialogContent-root": { padding: theme.spacing(2) },
			"& .MuiDialogActions-root": { padding: theme.spacing(1) },
		}));

		return (
			<>
			<Button size="small" onClick={handleClickOpen}>
				View Body
			</Button>
			<BootstrapDialog onClose={handleClose} aria-labelledby="view-reply-dialog" open={open}>
				<DialogContent dividers>
				{viewMode === "raw" ? (
					<Typography gutterBottom>
					<pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
						{params.value}
					</pre>
					</Typography>
				) : isHTML(params.value) ? (
					<div
					style={{ border: "1px solid #ddd", padding: "10px", borderRadius: "6px" }}
					dangerouslySetInnerHTML={{ __html: params.value }}
					/>
				) : (					
					<div
					style={{					
						padding: "10px",
						borderRadius: "6px",						
						whiteSpace: "pre-wrap",
					}}
					>
					{params.value}
					</div>
				)}
				</DialogContent>
				<DialogActions>
				<Button variant={viewMode === "raw" ? "contained" : "outlined"} onClick={() => setViewMode("raw")}>
					View Raw
				</Button>
				<Button variant={viewMode === "html" ? "contained" : "outlined"} onClick={() => setViewMode("html")}>
					View HTML
				</Button>
				<Button onClick={handleClose}>Close</Button>
				</DialogActions>
			</BootstrapDialog>
			</>
		);
	};


	const ApproveButton = (cellProps: CustomCellRendererProps) => {
		return (
			<Button color={"success"} size={"small"} startIcon={<Check/>} onClick={() => {
				approveOrRejectMutation.mutate({uid: cellProps.data.uid, type: cellProps.data.type, approved: true});
			}}>
				Approve
			</Button>
		)
	}

	const RejectButton = (cellProps: CustomCellRendererProps) => {
		return (
			<Button color={"error"} size={"small"} startIcon={<Close/>} onClick={() => {
				approveOrRejectMutation.mutate({uid: cellProps.data.uid, type: cellProps.data.type, approved: false});
			}}>
				Reject
			</Button>
		)
	}

	const columnCustomizerLabel = {		
		uid: "Message UID",
		user_email: "Email Address",
		blocked_reason: "Reason",
		subject: "Subject",
		body: "Body",				
	};

	const columnDefs: ColDef[] = [
		{field: "uid", headerName: "Message UID"},
		{field: "user_email", headerName: "Email Address"},		
		{field: "blocked_reason", headerName: "Reason"},
		{field: "subject", headerName: "Subject"},
		{field: "body", headerName: "Body", cellRenderer: BodyMessageDialog, sortable: false},
		{headerName: "Approve", cellRenderer: ApproveButton, sortable: false},
		{headerName: "Reject", cellRenderer: RejectButton, resizable: false, sortable: false},
	]

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (!pageData && pageDataQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>		
				<PaginatedServerSideDataTable
					columns={columnDefs}
					paginationData={pageData}
					loading={pageDataQuery.isLoading || pageDataQuery.isFetching}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					onSearchChange={handleSearchChange}
					searchPlaceholder="Search by UID, subject, email..."
					noRowsText="No pending requests."
					columnLabels={columnCustomizerLabel}
				/>
			</Box>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}
